import React, { useCallback, useRef } from 'react';
import toast, { ToastBar } from 'react-hot-toast';
import type { Toast, ToastPosition } from 'react-hot-toast';
import './ToastDismissibleBar.css';

const DRAG_THRESHOLD_PX = 72;
/** Por debajo de esto es un clic; evita “saltos” al mover un poco el dedo. */
const DRAG_ARM_PX = 10;

interface ToastDismissibleBarProps {
  toast: Toast;
  defaultPosition: ToastPosition;
}

/** Botón cerrar + arrastrar para descartar (react-hot-toast ToastBar estándar). */
export const ToastDismissibleBar: React.FC<ToastDismissibleBarProps> = ({
  toast: t,
  defaultPosition,
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ x: number; y: number; armed: boolean } | null>(null);

  const position = t.position ?? defaultPosition;

  const dismiss = useCallback(() => {
    toast.dismiss(t.id);
  }, [t.id]);

  const resetDragVisual = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    el.style.transition = 'transform 180ms ease, opacity 180ms ease';
    el.style.transform = '';
    el.style.opacity = '';
    window.setTimeout(() => {
      if (el) el.style.transition = '';
    }, 200);
  }, []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.rht-dismiss-x')) return;
      dragRef.current = { x: e.clientX, y: e.clientY, armed: false };
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
    },
    []
  );

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    const dist = Math.hypot(dx, dy);
    if (!d.armed) {
      if (dist < DRAG_ARM_PX) return;
      d.armed = true;
    }
    const el = wrapRef.current;
    if (el) {
      el.style.transition = 'none';
      el.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
      el.style.opacity = String(Math.max(0.25, 1 - dist / 280));
    }
  }, []);

  const onPointerEnd = useCallback(
    (e: React.PointerEvent) => {
      const d = dragRef.current;
      dragRef.current = null;
      if (!d) return;
      const dx = e.clientX - d.x;
      const dy = e.clientY - d.y;
      const dist = Math.hypot(dx, dy);
      if (d.armed && dist >= DRAG_THRESHOLD_PX) {
        dismiss();
        return;
      }
      if (d.armed) resetDragVisual();
    },
    [dismiss, resetDragVisual]
  );

  /** Tarjetas custom (alertas EU): ya traen su propio cierre; sin barra extra ni arrastre. */
  if (t.type === 'custom') {
    return (
      <ToastBar toast={t} position={position}>
        {({ message }) => message}
      </ToastBar>
    );
  }

  return (
    <div
      ref={wrapRef}
      className="rht-dismiss-wrap"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      style={{ touchAction: 'none' }}
    >
      <ToastBar toast={t} position={position}>
        {({ icon, message }) => (
          <>
            {icon}
            {message}
            <button
              type="button"
              className="rht-dismiss-x"
              aria-label="Cerrar notificación"
              title="Cerrar"
              onClick={(e) => {
                e.stopPropagation();
                dismiss();
              }}
            >
              ×
            </button>
          </>
        )}
      </ToastBar>
    </div>
  );
};
